import UserSettings from '../../utility/user-settings';
import EventDispatcher from '../../event/event-dispatcher';
import GeneralUtility from '../../utility/general-utility';
import ApiAction from './api-action';
import {FiltersResult} from '../../../types/filters-result';
import Plugin from '../plugin';
import TraceableError from '../../error/traceable-error';

/**
 * Base action for displaying filters for the Lawyer API.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class FiltersAction extends ApiAction {
  /**
   * Event for checking if the filters have changed.
   *
   * @type {EventDispatcher<any>}
   * @private
   */
  private _changeEvent = new EventDispatcher<any>();

  /**
   * Search parameters present in the URL. May be used to preselect certain search filters or similar.
   *
   * @type {URLSearchParams|null}
   * @private
   */
  private urlParameters: URLSearchParams = null;

  /**
   * Filter text fields.
   *
   * @type {NodeListOf<HTMLInputElement>}
   * @private
   */
  private textFields: NodeListOf<HTMLInputElement>;

  /**
   * Filter selects.
   *
   * @type {NodeListOf<HTMLSelectElement>}
   * @private
   */
  private selects: NodeListOf<HTMLSelectElement>;

  /**
   * Container of the alphabet buttons.
   *
   * @type {HTMLElement}
   * @private
   */
  private alphabet: HTMLElement;

  /**
   * Alphabet buttons.
   *
   * @type {NodeListOf<HTMLElement>}
   * @private
   */
  private alphabetButtons: NodeListOf<HTMLElement>;

  /**
   * Reset button.
   *
   * @type {HTMLElement}
   * @private
   */
  private resetButton: HTMLElement;

  /**
   * Search button.
   *
   * @type {HTMLElement}
   * @private
   */
  private searchButton: HTMLElement;


  protected constructor(plugin: Plugin, container: HTMLElement, endpoint: string) {
    super(plugin, container, endpoint);

    // If the filter action is called for the first time, make sure that the filter parameter is present
    const settings = UserSettings.getInstance().getPluginSettings(plugin.name);
    if (!settings.hasOwnProperty('filters')) {
      settings.filters = {};
      UserSettings.getInstance().setPluginSettings(plugin.name, settings);
    }
  }


  /**
   * Gets the event for checking if the filters have changed.
   *
   * @returns {EventDispatcher<any>}
   */
  get changeEvent(): EventDispatcher<any> {
    return this._changeEvent;
  }


  update(settings: any): void {
    // Check if any pre-selectable filter-parameters are present in the URL
    if (window.location.search) {
      this.urlParameters = new URLSearchParams(window.location.search);
    }

    // Create parameters for the API request
    // Do not add plugin settings, as the filter endpoint should not be filtered with the options present in there
    const parameters = this.createRequestParameters();

    // Notify before-update listeners
    this.beforeUpdateEvent.dispatch(parameters);

    fetch(parameters.url, {
        'method': 'GET'
      }
    ).then(response => {
      return response.json();
    })
      .then(result => {
        this.initializeResult(result, settings);
      })
      .catch(error => {
        throw new TraceableError('Updating filters failed.', error);
      });
  }


  protected initializeResult(result: FiltersResult, settings: any) {
    // Reset result container
    this.resultContainer.innerHTML = '';
    if (!result) {
      this.showNoResultContainer();
      return;
    }
    this.hideNoResultContainer();
  }


  /**
   * Initializes the filter controls.
   *
   * @returns {void}
   * @private
   */
  protected initializeControls(): void {
    // Button to reset the filters
    this.resetButton = this.container.querySelector('#reset');
    if (this.resetButton) {
      this.initializeResetButton();
    }

    // Button to perform the search
    this.searchButton = this.container.querySelector('button[type="submit"]');
    if (this.searchButton) {
      this.initializeSearchButton();
    }

    // Contains all text fields
    this.textFields = this.container.querySelectorAll('input[type="text"]');
    if (this.textFields) {
      this.initializeTextFields();
    }

    // Contains all selects
    this.selects = this.container.querySelectorAll('select');
    if (this.selects) {
      this.initializeSelects();
    }

    // Container of the alphabet buttons
    this.alphabet = this.container.querySelector('[data-alphabet]');
    if (this.alphabet) {
      // Contains all alphabet buttons
      this.alphabetButtons = this.alphabet.querySelectorAll('a');

      this.initializeAlphabet();
    }
  }


  /**
   * Resets all filters and controls.
   *
   * @returns {void}
   */
  protected reset(): void {
    if (this.textFields) {
      this.textFields.forEach(textField => {
        textField.value = '';
      });
    }

    if (this.selects) {
      this.selects.forEach(select => {
        select.value = '';
      });
    }

    if (this.alphabet) {
      // Mark alphabet "all" button as active
      this.deselectAlphabet();
      this.alphabetButtons[0].classList.add('active');
    }

    const settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
    settings.filters = {};
    this.changeEvent.dispatch(settings);
  }


  /**
   * Initializes all text fields.
   *
   * @returns {void}
   * @private
   */
  protected initializeTextFields(): void {
    this.textFields.forEach(textField => {
      const name = this.parseControlName(textField.getAttribute('name'));
      let settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);

      // Set saved value
      if (settings.filters[name]) {
        textField.value = settings.filters[name];
      }

      // If a parameter fitting this field is present in the URL, use it as a preselected value. Will override any saved value.
      if (this.urlParameters !== null && this.urlParameters.has(name)) {
        textField.value = decodeURIComponent(this.urlParameters.get(name));
        settings.filters[name] = textField.value.trim();
        this.changeEvent.dispatch(settings); // Trigger reload with preselected value
      }

      GeneralUtility.debouncedSearch(textField, () => {
        settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
        settings.filters[name] = textField.value.trim();
        this.changeEvent.dispatch(settings);
      });
    });
  }


  /**
   * Initializes all selects.
   *
   * @returns {void}
   * @private
   */
  protected initializeSelects() {
    this.selects.forEach(select => {
      const name = this.parseControlName(select.getAttribute('name'));
      let settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);

      // Set saved value
      if (settings.filters[name]) {
        select.value = settings.filters[name];
      }

      // If a parameter fitting this field is present in the URL, use it as a preselected value. Will override any saved value.
      if (this.urlParameters !== null && this.urlParameters.has(name)) {
        select.value = decodeURIComponent(this.urlParameters.get(name));
        settings.filters[name] = select.value.trim();
        this.changeEvent.dispatch(settings); // Trigger reload with preselected value
      }

      select.addEventListener('change', () => {
        settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
        settings.filters[name] = select.value.trim();
        this.changeEvent.dispatch(settings);
      });
    });
  }


  /**
   * Initializes the alphabet selection.
   *
   * @returns {void}
   * @private
   */
  protected initializeAlphabet(): void {
    const name = this.alphabet.dataset.alphabet;

    this.alphabetButtons.forEach(button => {
      const value = button.dataset.value;
      let settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);

      // Set saved value
      if (value === settings.filters[name]) {
        this.deselectAlphabet();
        button.classList.add('active');
      }

      button.addEventListener('click', e => {
        e.preventDefault();

        // Mark selected button as active
        this.deselectAlphabet();
        button.classList.add('active');

        // Add selected character to the filter
        settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);

        if (value === undefined) {
          delete settings.filters[name];
        } else {
          settings.filters[name] = value;
        }

        this.changeEvent.dispatch(settings);
      });
    });
  }


  /**
   * Deselects the alphabet selection.
   *
   * @returns {void}
   * @private
   */
  protected deselectAlphabet(): void {
    this.alphabetButtons.forEach(button => {
      button.classList.remove('active');
    });
  }


  /**
   * Initializes the button to reset the filters.
   *
   * @returns {void}
   * @private
   */
  protected initializeResetButton(): void {
    this.resetButton.addEventListener('click', e => {
      e.preventDefault();
      this.reset();
    });
  }


  /**
   * Initializes the button to perform the search.
   *
   * @returns {void}
   * @private
   */
  protected initializeSearchButton(): void {
    this.searchButton.addEventListener('click', e => {
      e.preventDefault();
      const settings = UserSettings.getInstance().getPluginSettings(this.plugin.name);
      this.changeEvent.dispatch(settings);
    });
  }


  /**
   * Parses a control name. Basically removes any namespacing and returns the last name parameter.
   *
   * @param {string} fullName Full control name with namespacing
   * @returns {string} Parsed control name
   * @private
   */
  protected parseControlName(fullName: string): string {
    return fullName.split('[').pop().replace(']', '');
  }
}
